"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { MDBDataTable } from "mdbreact"
import { Dropdown, DropdownButton, ButtonGroup } from "react-bootstrap"
import { TfiMoreAlt } from "react-icons/tfi"
import { FaUsers, FaUserCheck, FaTicketAlt } from "react-icons/fa"
import UpdateAttendeeModal from "./UpdateAttendeeModel"
import PreviewAttendeeModal from "./Previewattendee"
import { useUser } from "../../AdminUsers/UserContext"

class ErrorBoundary extends React.Component {
  state = { hasError: false }
  static getDerivedStateFromError() {
    return { hasError: true }
  }
  render() {
    if (this.state.hasError) {
      return <div>Error loading table. Please try again.</div>
    }
    return this.props.children
  }
}

const AttendeesList = ({
  attendeesList,
  updateAttendeeStatus,
  resendSuccessMail,
  getAttendeesByEventID,
  ticketList = [],
  eventCountList = {},
  attendeesListSummary = [],
  eventId,
}) => {
  const { checkPermission } = useUser()
  const [activeTab, setActiveTab] = useState("confirmed")
  const [rows, setRows] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [selectedPreviewAttendee, setSelectedPreviewAttendee] = useState({})
  const [selectedAttendee, setSelectedAttendee] = useState({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [expandedAttendees, setExpandedAttendees] = useState(new Set())
  const tableRef = useRef(null)
  const currentPageRef = useRef(0)

  const fetchAttendees = useCallback(() => {
    getAttendeesByEventID({
      status: activeTab,
      search: searchQuery,
    })
  }, [activeTab, searchQuery, getAttendeesByEventID])

  useEffect(() => {
    fetchAttendees()
  }, [activeTab, searchQuery])

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  const toggleExpand = useCallback((id) => {
    setExpandedAttendees((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  const handleShowAddAttendeeModal = useCallback((show, attendee = null) => {
    setShowAddModal(show)
    if (attendee) {
      setSelectedAttendee(attendee)
    }
  }, [])

  const [handleActionStatus, setHandleAction] = useState(false)
  const handleAction = useCallback(
    async (row, action) => {
      const hasPermission = await checkPermission("Attendees", "update")
      if (!hasPermission) return
      if (action === "resend") {
        setHandleAction(true);
        console.log(`Resend email to ${row.name || "Attendee"}`)
        await resendSuccessMail(row.id)
        setHandleAction(false);
      } else if (action === "cancel") {
        const success = await updateAttendeeStatus(row.id, "cancelled")
        if (success) {
          fetchAttendees()
        }
      } else if (action === "edit") {
        handleShowAddAttendeeModal(true, row)
      }
    },
    [updateAttendeeStatus, handleShowAddAttendeeModal, fetchAttendees, checkPermission, resendSuccessMail],
  )

  const handleRowClick = useCallback(
    (row, isGuest = false, guestData = null) => {
      let customFields = []
      if (row.form_data && row.form_data !== "null") {
        try {
          customFields = JSON.parse(row.form_data)[0]?.custom_fields || []
        } catch (e) {
          console.error("Error parsing form_data", e)
        }
      }
      const attendeeData = isGuest
        ? {
          ...row,
          ...guestData,
          eventId: eventId || row.event_id || "",
          name: `${guestData.first_name || "Guest"} ${guestData.last_name || ""}`.trim(),
          registrationId: guestData.reg_code || "N/A",
          orderId: row.order_id || "N/A",
          ticketType: "Accompanying Person",
          registeredOn: row.created_at || "",
          email: guestData.email || "N/A",
          customFields: guestData.custom_fields || [],
          isGuest: true,
        }
        : {
          ...row,
          eventId: eventId || row.event_id || "",
          name: `${row.salutation || ""} ${row.first_name || "Unknown"} ${row.last_name || ""}`.trim(),
          registrationId: row.reg_code || "N/A",
          orderId: row.order_id || "N/A",
          ticketType: row.ticket_name || "N/A",
          registeredOn: row.created_at || "",
          email: row.email || "N/A",
          customFields,
          isGuest: false,
        }
      setSelectedPreviewAttendee(attendeeData)
      setShowPreviewModal(true)
    },
    [eventId],
  )

  useEffect(() => {
    const table = tableRef.current
    if (table && table.table) {
      const handlePageChange = (data) => {
        currentPageRef.current = data.activePage
      }
      table.table.on("page.dt", () => {
        const info = table.table.page.info()
        handlePageChange({ activePage: info.page })
      })
      return () => {
        if (table.table) {
          table.table.off("page.dt")
        }
      }
    }
  }, [])

  useEffect(() => {
    console.log(`Building rows for ${activeTab} tab, attendeesList:`, attendeesList)
    const builtRows = []
      ; (attendeesList || [])
        .filter((row) => row && row.id)
        .forEach((row, originalIndex) => {
          if (!row || !row.id) {
            console.warn(`Skipping invalid attendee row at index ${originalIndex}:`, row)
            return
          }

          let guests = []
          if (row.guest_data && row.guest_data !== "null" && row.guest_data !== "[]") {
            try {
              guests = JSON.parse(row.guest_data) || []
              if (!Array.isArray(guests)) {
                console.warn(`guest_data is not an array for attendee ${row.id}:`, guests)
                guests = []
              }
            } catch (e) {
              console.error(`Error parsing guest_data for attendee ${row.id}:`, e)
              guests = []
            }
          }
          const hasGuests = Array.isArray(guests) && guests.length > 0
          const isExpanded = expandedAttendees.has(row.id)

          let mainName = `${row.salutation || ""} ${row.first_name || ""} ${row.last_name || ""}`.trim()
          if (!mainName && row.form_data && row.form_data !== "null") {
            try {
              const formData = JSON.parse(row.form_data)?.[0]?.custom_fields || []
              const textField = formData.find((f) => f?.type === "text" && f?.value)?.value
              mainName = textField || "Pending Attendee"
            } catch (e) {
              console.error(`Error parsing form_data for name for attendee ${row.id}:`, e)
              mainName = "Pending Attendee"
            }
          }
          mainName = mainName || "Unknown Attendee"
          const regCodeText = row.reg_code ? ` (Reg Code: ${row.reg_code})` : ""
          const ticketName = row.ticket_name || "Unknown Ticket"
          const guestToggleText = hasGuests ? `+${guests.length} guests ${isExpanded ? "Hide ▼" : ""}` : ""
          const ticketsValue = ticketName + (guestToggleText ? ` | ${guestToggleText}` : "")

          const mainRow = {
            name: mainName + regCodeText,
            registerDate: row.created_at
              ? new Date(row.created_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              : "--",
            tickets: ticketsValue,
            email: row.email || "--",
            mobile: JSON.parse(row.form_data)?.[0]?.custom_fields.find((f) => f?.name === "phone_number" && f?.value)?.value || "--",
            actions: (
              <Dropdown as={ButtonGroup} key={`dropdown-${row.id}`} onClick={(e) => e.stopPropagation()}>
                <DropdownButton
                  variant="light"
                  id={`dropdown-actions-${row.id}`}
                  title={<TfiMoreAlt />}
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Dropdown.Item
                    key={`edit-${row.id}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAction(row, "edit")
                    }}
                  >
                    Edit
                  </Dropdown.Item>
                  <Dropdown.Item
                    key={`resend-${row.id}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAction(row, "resend")
                    }}
                  >
                    Resend Success Email
                  </Dropdown.Item>
                  {activeTab !== "cancelled" && (
                    <Dropdown.Item
                      key={`cancel-${row.id}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAction(row, "cancel")
                      }}
                    >
                      Cancel Ticket
                    </Dropdown.Item>
                  )}
                </DropdownButton>
              </Dropdown>
            ),
            clickEvent: (e) => {
              if (
                e.target.closest(".dropdown") ||
                e.target.closest(".dropdown-toggle") ||
                e.target.closest(".dropdown-menu") ||
                e.target.closest(".toggle-guest")
              ) {
                return
              }
              handleRowClick(row)
            },
            guestToggle: hasGuests ? () => toggleExpand(row.id) : null,
          }

          builtRows.push(mainRow)

          if (hasGuests && isExpanded) {
            guests.forEach((guest, guestIndex) => {
              const guestCustomFields = Array.isArray(guest?.custom_fields) ? guest.custom_fields : []
              const guestFirstName = guestCustomFields.find((f) => f?.name === "first_name")?.value || "Guest"
              const guestLastName = guestCustomFields.find((f) => f?.name === "last_name")?.value || ""
              const guestEmail = guestCustomFields.find((f) => f?.name === "email_address")?.value || "--"
              const guestPhone = guestCustomFields.find((f) => f?.name === "phone_number")?.value || "--"
              const guestRegCode = guest?.reg_code ? ` (Reg Code: ${guest.reg_code})` : ""

              const guestRow = {
                checkbox: "",
                name: `${guestFirstName} ${guestLastName}`.trim() || "Guest" + guestRegCode,
                registerDate: row.created_at
                  ? new Date(row.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                  : "--",
                tickets: "Accompanying Person",
                email: guestEmail,
                actions: "",
                className: "guest-row",
                clickEvent: (e) => {
                  if (
                    e.target.closest(".dropdown") ||
                    e.target.closest(".dropdown-toggle") ||
                    e.target.closest(".dropdown-menu") ||
                    e.target.closest(".toggle-guest")
                  ) {
                    return
                  }
                  handleRowClick(row, true, {
                    first_name: guestFirstName,
                    last_name: guestLastName,
                    email: guestEmail,
                    phone: guestPhone,
                    reg_code: guest.reg_code,
                    custom_fields: guestCustomFields,
                  })
                },
              }

              builtRows.push(guestRow)
            })
          }
        })

    setRows((prevRows) => {
      if (JSON.stringify(prevRows) !== JSON.stringify(builtRows)) {
        setTimeout(() => {
          if (tableRef.current && tableRef.current.table) {
            tableRef.current.table.page(currentPageRef.current).draw("page")
          }
        }, 0)
        return builtRows
      }
      return prevRows
    })
    console.log(`Built rows for ${activeTab} tab:`, builtRows)
  }, [activeTab, attendeesList, expandedAttendees, handleAction, handleRowClick, toggleExpand, eventId])

  const columns = [
    {
      label: "Name",
      field: "name",
      sort: "asc",
      attributes: {
        style: { width: "20%", fontWeight: "bold" },
      },
    },
    {
      label: "Registration Date",
      field: "registerDate",
      sort: "asc",
      attributes: {
        style: { width: "12%", fontWeight: "bold" },
      },
    },
    {
      label: "Tickets",
      field: "tickets",
      sort: "asc",
      attributes: {
        style: { width: "15%", fontWeight: "bold" },
      },
    },
    {
      label: "Email",
      field: "email",
      sort: "asc",
      attributes: {
        style: { width: "20%", fontWeight: "bold" },
      },
    },
    {
      label: "Mobile",
      field: "mobile",
      sort: "asc",
      attributes: {
        style: { width: "20%", fontWeight: "bold" },
      },
    },
    {
      label: "Actions",
      field: "actions",
      sort: "disabled",
      attributes: {
        style: { width: "5%" },
      },
    },
  ]

  const tableData = useMemo(
    () => ({
      columns,
      rows: rows
        .filter((row) => row && typeof row === "object")
        .map((row, index) => {
          if (!row.name || !row.registerDate || !row.tickets || !row.email || !row.actions) {
            console.warn(`Invalid row data at index ${index} for ${activeTab} tab:`, row)
            return {
              name: row.name || "Unknown Attendee",
              registerDate: row.registerDate || "--",
              tickets: typeof row.tickets === "string" ? row.tickets : "Unknown Ticket",
              email: row.email || "--",
              mobile: row.email || "--",
              actions: row.actions || "",
              clickEvent: row.clickEvent || (() => { }),
              className: row.className || "",
              guestToggle: row.guestToggle || null,
            }
          }
          return {
            ...row,
            clickEvent: row.clickEvent || (() => { }),
            tickets:
              row.guestToggle && typeof row.tickets === "string" && row.tickets.includes("|") ? (
                <div>
                  {row.tickets.split(" | ")[0] || "Unknown Ticket"}
                  <small
                    onClick={row.guestToggle}
                    style={{ cursor: "pointer", color: "purple", display: "block" }}
                    className="toggle-guest"
                  >
                    {row.tickets.split(" | ")[1] || ""}
                  </small>
                </div>
              ) : typeof row.tickets === "string" ? (
                row.tickets
              ) : (
                "Unknown Ticket"
              ),
          }
        }),
      footer: false,
    }),
    [rows, activeTab],
  )

  console.log(`Rendering tableData for ${activeTab} tab:`, tableData)

  const renderTabs = () => {
    const statusCounts = (attendeesListSummary || []).reduce(
      (acc, { status, count }) => ({ ...acc, [status]: count }),
      { confirmed: 0, pending: 0, blacklisted: 0, cancelled: 0 },
    )

    const tabs = [
      { key: "confirmed", title: "Confirmed", count: statusCounts.confirmed },
      // { key: "pending", title: "Pending", count: statusCounts.pending },
      { key: "blacklisted", title: "Blacklisted", count: statusCounts.blacklisted },
      { key: "cancelled", title: "Cancelled", count: statusCounts.cancelled },
    ]

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "15px 24px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          marginBottom: "22px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: "12px 20px",
                    border: "none",
                    backgroundColor: isActive ? "#184778" : "transparent",
                    color: isActive ? "#ffffff" : "#666666",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    borderRadius: "8px",
                    transition: "all 0.2s ease-in-out",
                    marginRight: "8px"
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.target.style.backgroundColor = "#f3f4f6"
                      e.target.style.color = "#184778"
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.target.style.backgroundColor = "transparent"
                      e.target.style.color = "#666666"
                    }
                  }}
                >
                  {tab.title} ({tab.count})
                </button>
              )
            })}
          </div>

          <div style={{ position: "relative" }}>
            <i
              className="fa fa-search"
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666666",
                fontSize: "14px",
              }}
            ></i>
            <input
              type="text"
              placeholder="Search Attendees..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: "10px 16px 10px 36px",
                border: "2px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                width: "250px",
                transition: "border-color 0.2s ease-in-out",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#184778")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderStatsCards = () => {
    if (!eventCountList) return null

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "24px",
          marginBottom: "22px",
        }}
      >
        <div className="order_inner_card" >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="order_inner_card_div_image">
              <FaUsers />
            </div>
            <div className="order_inner_card_div">
              <h4>
                Total Attendees
              </h4>
              <p>
                {eventCountList.total_attendees || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="order_inner_card" >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="order_inner_card_div_image">
              <FaUserCheck />
            </div>
            <div className="order_inner_card_div">
              <h4>
                Guests
              </h4>
              <p>
                {eventCountList.guest_count || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="order_inner_card" >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div className="order_inner_card_div_image">
              <FaTicketAlt />
            </div>
            <div className="order_inner_card_div">
              <h4 >
                Total Tickets
              </h4>
              <p >
                {eventCountList.total_ticket_quantity || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderAttendeesTable = () => {
    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          padding: "32px",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <ErrorBoundary>
            <MDBDataTable
              ref={tableRef}
              noBottomColumns
              className="table table-hover js-basic-example dataTable table-custom spacing5"
              entriesOptions={[5, 10, 20, 25]}
              entries={10}
              paging
              pagesAmount={5}
              data={tableData}
              sortRows={["name"]}
              preserveScroll={true}
              preserveState={true}
              searching={false}
            />
          </ErrorBoundary>
        </div>

        <div
          style={{
            marginTop: "24px",
            fontSize: "14px",
            color: "#666666",
            textAlign: "center",
          }}
        >
          {`Showing 1 to ${rows.length} records of ${(attendeesListSummary.find((s) => s.status === activeTab) || {}).count || 0
            }`}
        </div>
      </div>
    )
  }

  const renderMainContent = () => {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f9fafb",
        }}
      >
        <div>
          {renderStatsCards()}
          {renderTabs()}

          {renderAttendeesTable()}
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    )
  }

  return (
    <>
      {renderMainContent()}
      <UpdateAttendeeModal
        ticketList={ticketList}
        attendeeDetails={selectedAttendee}
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        getAttendeesByEventID={getAttendeesByEventID}
        activeTab={activeTab}
      />
      <PreviewAttendeeModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        attendee={selectedPreviewAttendee}
        onEdit={(attendee) => handleAction(attendee, "edit")}
        onMoreAction={handleAction}
        handleActionStatus={handleActionStatus}
      />
    </>
  )
}

export default AttendeesList
